import { CustomError } from '@/services/api/errors/APIError';
import { useVariable } from '@softwareimaging/backstage';
import ErrorStackParser from 'error-stack-parser';
import { FC, PropsWithChildren, useState } from 'react';
import { FallbackProps, ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary';
import { FiCheck, FiCopy } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import Balancer from 'react-wrap-balancer';
import { ZodError, ZodIssue } from 'zod';
import { Button } from '../components/form/Button';
import { Variables } from '../services/backstage/config';
import { useAppInsights } from '@/services/insights/useAppInsights';
import { ButtonVariant } from '@/components/form/buttonHelper';

interface ErrorProps {
  error: Error;
}

const Explanation: FC<PropsWithChildren> = ({ children }) => (
  <div className="bg-primary-std flex w-full flex-col gap-1.5 rounded-md p-5 text-left leading-5">{children}</div>
);

const Frame = ({ frame }: { frame: ErrorStackParser.StackFrame }) => {
  return (
    <div className="my-2 flex flex-col rounded-lg bg-white p-4">
      <p className="text-sm">
        <label className="mr-1 text-sm font-medium">Function:</label>
        <code>{frame.functionName}</code>
      </p>
      {frame.fileName && (
        <p className="text-sm">
          <label className="mr-1 text-sm font-medium">File:</label>
          <code>{new URL(frame.fileName).pathname}</code>
        </p>
      )}
      <div className="flex gap-3">
        <p className="text-sm">
          <label className="mr-1 text-sm font-medium">Line:</label>
          <code>{frame.lineNumber}</code>
        </p>
        <p className="text-sm">
          <label className="mr-1 text-sm font-medium">Column:</label>
          <code>{frame.columnNumber}</code>
        </p>
      </div>
    </div>
  );
};

export const ErrorExplanation: FC<ErrorProps> = ({ error }) => {
  const [currentDate] = useState(new Date());
  const stack = ErrorStackParser.parse(error);
  const frame = stack.at(0);

  return (
    <Explanation>
      <h2 className="font-medium">
        {`Error ${frame?.functionName && `at ${formatFunctionName(frame.functionName)}`}`}
      </h2>
      {frame && import.meta.env.DEV && <Frame frame={frame} />}
      <p className="text-sm">
        This error is in a raw format which means it is not handled by the application. Please contact support when
        possible about this problem.
      </p>
      <p>
        <label className="mr-1 text-sm font-medium">Message:</label>
        <code>{error.message}</code>
      </p>
      <code className="text-secondary-std text-xs font-medium">{currentDate.toISOString()}</code>
    </Explanation>
  );
};

interface CustomErrorProps {
  error: CustomError;
}

export const CustomErrorExplanation: FC<CustomErrorProps> = ({ error }) => {
  const [currentDate] = useState(new Date());
  const stack = ErrorStackParser.parse(error);
  const frame = stack.at(3);

  return (
    <Explanation>
      <h2 className="font-medium">{error.name}</h2>
      {frame && import.meta.env.DEV && <Frame frame={frame} />}
      <p>
        <label className="mr-1 text-sm font-medium">Message:</label>
        {error.message}
      </p>
      <code className="text-secondary-std text-xs font-medium">{currentDate.toISOString()}</code>
    </Explanation>
  );
};

interface ValidationErrorProps {
  error: ZodError;
}

const Issue: FC<ZodIssue> = ({ path, message }) => {
  return (
    <div>
      <p className="text-sm">
        <label className="mr-1 font-medium">Issue:</label>
        Property {path.join('.')} {message === 'Required' ? ' is required' : `: ${message}`}
      </p>
    </div>
  );
};

const getValidationStackFrame = (stack: ErrorStackParser.StackFrame[]) => {
  const index = stack.findIndex(frame => frame.functionName?.includes('.parse'));
  if (index === -1) return;
  return stack[index + 1];
};

const formatFunctionName = (name: string) => {
  const firstLetter = name.charAt(0);
  if (firstLetter === firstLetter.toUpperCase()) return `<${name} />`;
  return `${name}()`;
};

export const ValidationErrorExplanation: FC<ValidationErrorProps> = ({ error }) => {
  const [currentDate] = useState(new Date());
  const stack = ErrorStackParser.parse(error);
  const frame = getValidationStackFrame(stack);
  return (
    <Explanation>
      <h2 className="font-medium">
        Validation error{' '}
        {frame?.functionName && (
          <>
            at <code>{formatFunctionName(frame.functionName)}</code>
          </>
        )}
      </h2>
      <div>{error.issues.map(Issue)}</div>
      <code className="text-secondary-std text-xs font-medium">{currentDate.toISOString()}</code>
    </Explanation>
  );
};

export const SessionReference: FC = () => {
  const { getSessionId } = useAppInsights(state => state);
  const [clipboardStatus, setClipboardStatus] = useState<'idle' | 'copied'>('idle');

  const copyToClipboard = async (text: string) => {
    await navigator.clipboard.writeText(text);
    setClipboardStatus('copied');
    setTimeout(() => setClipboardStatus('idle'), 2000);
  };

  const sessionId = getSessionId();
  if (!sessionId) {
    return null;
  }

  return (
    <button
      onClick={() => copyToClipboard(sessionId)}
      className="bg-primary-std text-secondary-std hover:text-primary-std relative flex items-center gap-2 overflow-clip rounded-md px-3 py-2 text-sm shadow-sm"
    >
      <span>
        Reference: <span className="font-medium">{sessionId}</span>
      </span>
      <FiCopy />
      {clipboardStatus === 'copied' && (
        <span className="bg-primary-std text-primary-std absolute inset-0 flex items-center justify-center gap-1 text-sm font-medium">
          <FiCheck />
          <span>Copied!</span>
        </span>
      )}
    </button>
  );
};

const getExplanationForError = (error: Error) => {
  if (error instanceof CustomError) {
    return <CustomErrorExplanation error={error} />;
  }
  if (error instanceof ZodError) {
    return <ValidationErrorExplanation error={error} />;
  }
  return <ErrorExplanation error={error} />;
};

export const CriticalErrorBoundary: FC<PropsWithChildren> = ({ children }) => {
  const Fallback: FC<FallbackProps> = ({ error, resetErrorBoundary }) => {
    const email = useVariable<Variables>('supportEmail');
    return (
      <div className="text-primary-std flex h-screen w-screen items-center justify-center">
        <div className="flex w-full max-w-lg flex-col items-center justify-center gap-8 text-center">
          <div className="flex flex-col gap-2">
            <h1 className="text-3xl font-bold leading-6">
              <Balancer>A problem has occurred</Balancer>
            </h1>
            <p className="leading-5">
              <Balancer>
                {`We're sorry, we've encountered a problem that has stopped the application working as expected.`}
              </Balancer>
            </p>
          </div>
          {getExplanationForError(error)}
          <div className="flex flex-col gap-3">
            <p className="leading-5">
              <Balancer>
                If this is your first time having problems please try again to see if the problem is resolved.
              </Balancer>
            </p>
            <Link onClick={resetErrorBoundary} to="/auth/signout">
              <Button className="w-full" variant={ButtonVariant.Mono}>
                Sign out & try again
              </Button>
            </Link>
          </div>
          <div className="flex flex-col items-center gap-3">
            <p className="leading-5">
              <Balancer>
                If you continue to encounter a problem please contact support with the session reference below.
              </Balancer>
            </p>
            <SessionReference />
            <a target="_blank" onClick={resetErrorBoundary} href={`mailto:${email}`} rel="noreferrer">
              <Button variant={ButtonVariant.Mono}>Contact support</Button>
            </a>
          </div>
        </div>
      </div>
    );
  };

  const trackException = useAppInsights(state => state.trackException);

  return (
    <ReactErrorBoundary onError={trackException} FallbackComponent={Fallback}>
      {children}
    </ReactErrorBoundary>
  );
};
